import { Component, AfterViewInit, ElementRef, HostListener, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { environment } from '../../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { ModalGeneralComponent } from '../../../dashboard/user/modals/modal-general/modal-general.component';
import { Observable, startWith, map } from 'rxjs';
import { COUNTRIES, Country, defaultCountry } from '../../../utils/countries';

@Component({
  selector: 'app-compass-login',
  standalone: true,
  imports: [ ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatAutocompleteModule, MatButtonModule, CommonModule, MatCheckboxModule, TranslateModule],
  templateUrl: './compass-login.component.html',
  styleUrl: './compass-login.component.scss'
})
export class CompassLoginComponent implements AfterViewInit, OnInit {

  registerForm: FormGroup;
  termsForm: FormGroup;
  isLoading: boolean = false;
  url_link: string = '';
  errorMessage: string = '';

  countries: Country[] = COUNTRIES;
  filteredCountries$!: Observable<Country[]>;

  constructor(private fb: FormBuilder, private http: HttpClient, private route: Router, private translate: TranslateService, private dialog: MatDialog, private elementRef: ElementRef) {
    this.registerForm = this.fb.group({
      name: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      email_repeat: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required]],
      password_repeat: ['', [Validators.required]],
      country: [defaultCountry(), [Validators.required, this.validCountry()]],
      phone: ['', [Validators.required, Validators.pattern(/^[\d\s-]{6,20}$/)]],
      linkedin_url: [''],
    });

    // Separate form for checkbox with autocomplete off
    this.termsForm = this.fb.group({
      terms: [false, Validators.requiredTrue],
    });

    // cross-field validators
    this.registerForm.setValidators([
      this.matchFieldsValidator('email', 'email_repeat', 'emailMismatch'),
      this.matchFieldsValidator('password', 'password_repeat', 'passwordMismatch'),
    ]);

    this.registerForm.get('email')?.valueChanges.subscribe(() => {
      this.registerForm.get('email_repeat')?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
      this.registerForm.updateValueAndValidity({ onlySelf: false, emitEvent: false });
    });
    this.registerForm.get('email_repeat')?.valueChanges.subscribe(() => {
      this.registerForm.updateValueAndValidity({ onlySelf: false, emitEvent: false });
    });
    this.registerForm.get('password')?.valueChanges.subscribe(() => {
      this.registerForm.get('password_repeat')?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
      this.registerForm.updateValueAndValidity({ onlySelf: false, emitEvent: false });
    });
    this.registerForm.get('password_repeat')?.valueChanges.subscribe(() => {
      this.registerForm.updateValueAndValidity({ onlySelf: false, emitEvent: false });
    });

    // we get the link from state
    if (history.state && history.state.link) {
      this.url_link = history.state.link;
      console.log('Link from state:', this.url_link);
    }

    console.log('Link from state (saved):', this.url_link);
  }

  ngOnInit() {
    this.filteredCountries$ = this.registerForm.get('country')!.valueChanges.pipe(
      startWith(this.registerForm.get('country')!.value),
      map(v => this.filterCountries(v)),
    );
  }

  ngAfterViewInit() {
    // Sync form values with any browser-restored input values
    setTimeout(() => {
      this.syncFormWithInputValues();
    }, 0);
    
    // Always reset checkbox to false to prevent browser restoration
    this.termsForm.patchValue({ terms: false });
  }

  displayCountry = (c: Country | string | null): string => {
    if (!c) return '';
    if (typeof c === 'string') return c;
    return c.dialCode;
  };

  trackByCode = (_: number, c: Country) => c.code;

  onCountrySelected(c: Country) {
    this.registerForm.get('country')!.setValue(c, { emitEvent: false });
  }

  countryName(c: Country): string {
    const lang = this.translate.currentLang || this.translate.getDefaultLang();
    return lang === 'es' ? c.nameEs : c.name;
  }

  private filterCountries(value: Country | string | null): Country[] {
    if (!value) return this.countries;
    if (typeof value === 'object') return this.countries;

    const q = value.toString().toLowerCase().trim().replace(/\s+/g, '');
    if (!q) return this.countries;

    const isEs = (this.translate.currentLang || this.translate.getDefaultLang()) === 'es';

    return this.countries.filter(c => {
      const localName = (isEs ? c.nameEs : c.name).toLowerCase();
      const altName = (isEs ? c.name : c.nameEs).toLowerCase();
      return (
        localName.includes(q) ||
        altName.includes(q) ||
        c.code.includes(q) ||
        c.dialCode.toLowerCase().includes(q) ||
        c.dialCode.replace('+', '').includes(q.replace('+', ''))
      );
    });
  }

  private validCountry() {
    return (control: AbstractControl): ValidationErrors | null => {
      const v = control.value;
      if (v && typeof v === 'object' && 'dialCode' in v) return null;
      return { invalidCountry: true };
    };
  }

  private syncFormWithInputValues(isFocused: boolean = false) {
    // Get all form control names and sync their values
    const formControls = ['name', 'email', 'email_repeat', 'password', 'password_repeat', 'phone', 'linkedin_url'];
    
    formControls.forEach(controlName => {
      const input = this.elementRef.nativeElement.querySelector(`[formControlName="${controlName}"]`) as HTMLInputElement;
      if (input && input.value && input.value !== this.registerForm.get(controlName)?.value) {
        this.registerForm.patchValue({ [controlName]: input.value });
      }
    });

  }

  @HostListener('focusin', ['$event'])
  onFocusIn(event: FocusEvent) {
    // Sync form values when user focuses on inputs (helps with browser autofill)
    setTimeout(() => {
      this.syncFormWithInputValues(true);
    }, 100);
  }

  private matchFieldsValidator(primaryKey: string, repeatKey: string, errorKey: string): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const primary = group.get(primaryKey);
      const repeat = group.get(repeatKey);
      if (!primary || !repeat) return null;

      const mismatch = primary.value !== repeat.value;

      // set error on the repeat control only
      if (mismatch) {
        const existingErrors = repeat.errors || {};
        if (!existingErrors[errorKey]) {
          repeat.setErrors({ ...existingErrors, [errorKey]: true });
        }
      } else {
        if (repeat.errors) {
          const { [errorKey]: removed, ...rest } = repeat.errors;
          repeat.setErrors(Object.keys(rest).length ? rest : null);
        }
      }

      return null;
    };
  }

  register() {
    // Combine both forms for validation and submission
    const isFormValid = this.registerForm.valid && this.termsForm.valid;

    if (isFormValid) {
      this.isLoading = true;

      const v = this.registerForm.value;
      const country: Country = v.country;
      const phoneDigits = (v.phone ?? '').toString().replace(/\D/g, '');

      const formData: any = {
        name: v.name,
        email: v.email,
        email_repeat: v.email_repeat,
        password: v.password,
        password_repeat: v.password_repeat,
        phone: `${country.dialCode}${phoneDigits}`,
        linkedin_url: v.linkedin_url,
        ...this.termsForm.value,
        comes_from_ad: true,
      };

      this.http.post(`${environment.url}/api/company/register`, formData).subscribe({
        next: (response: any) => {
          this.isLoading = false;

            // we divide the link by locked_prefilled_email=
            const url_parts = this.url_link.split('locked_prefilled_email=');
            this.url_link = url_parts[0] + 'locked_prefilled_email=' + this.registerForm.get('email')?.value;
            console.log('Link from state changed:', this.url_link);

            // we add the access_token to the cookie
            document.cookie = `access_token=${response.access_token}; path=/;`;
            document.cookie = `company_name=${this.registerForm.get('name')?.value}; path=/;`;

            (window as any).dataLayer.push({ 
              event: "account_created",
              status: "200",     // Status de error o exito de registro
            });

            window.location.href = this.url_link;
        },
        error: (error: any) => {
          console.error('Error:', error);
          this.isLoading = false;
          if (error.status === 400) {
            if (error.error && error.error.code === 'EMAIL_ALREADY_EXISTS') {
              
              ModalGeneralComponent.open(this.dialog, {
                title: this.translate.instant('companyRegister.modals.emailExists.title'),
                message: this.translate.instant('companyRegister.modals.emailExists.message'),
                buttonText: this.translate.instant('companyRegister.modals.emailExists.buttonText'),
                type: 'error',
                specific: true,
                link: '/company/login?redirect_to_buy=1'
              });


            } else if (error.error && error.error.code === 'MISSING_INFO') {
              this.errorMessage = this.translate.instant('companyRegister.errors.missingInfo');
            } else {
              this.errorMessage = this.translate.instant('companyRegister.errors.unknownError');
            }
          }
        }
      }, );
    } else {
      this.registerForm.markAllAsTouched();
      this.termsForm.markAllAsTouched();
      console.error('Form is invalid');
    }
  }
}
